import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Pencil, Plus } from "lucide-react"
import { type FormEvent, useState } from "react"

import { api, ErrorApi } from "@/api/cliente"
import { Boton } from "@/componentes/ui/Boton"
import { EstadoCarga, EstadoError, EstadoVacio } from "@/componentes/ui/Estados"
import { Insignia } from "@/componentes/ui/Insignia"
import { Modal } from "@/componentes/ui/Modal"

import { Documentos } from "./Documentos"
import type { Chofer, Empresa, Vehiculo } from "./tipos"

export type Clase = "EMPRESA" | "VEHICULO" | "CHOFER"

type Campo = {
  clave: string
  etiqueta: string
  tipo?: "texto" | "fecha" | "numero" | "empresa" | "correo"
  requerido?: boolean
}

const CAMPOS: Record<Clase, Campo[]> = {
  EMPRESA: [
    { clave: "razon_social", etiqueta: "Razón social", requerido: true },
    { clave: "rif", etiqueta: "RIF", requerido: true },
    { clave: "domicilio", etiqueta: "Domicilio fiscal" },
    { clave: "telefono", etiqueta: "Teléfono" },
    { clave: "correo", etiqueta: "Correo", tipo: "correo" },
  ],
  VEHICULO: [
    { clave: "empresa_id", etiqueta: "Empresa de transporte", tipo: "empresa", requerido: true },
    { clave: "placa", etiqueta: "Placa del chuto", requerido: true },
    { clave: "placa_remolque", etiqueta: "Placa del remolque o batea" },
    { clave: "marca", etiqueta: "Marca" },
    { clave: "modelo", etiqueta: "Modelo" },
    { clave: "color", etiqueta: "Color" },
    { clave: "anio", etiqueta: "Año", tipo: "numero" },
  ],
  CHOFER: [
    { clave: "nombres", etiqueta: "Nombres", requerido: true },
    { clave: "apellidos", etiqueta: "Apellidos", requerido: true },
    { clave: "cedula", etiqueta: "Cédula", requerido: true },
    { clave: "telefono", etiqueta: "Teléfono" },
    { clave: "licencia_grado", etiqueta: "Grado de la licencia" },
    { clave: "licencia_vence", etiqueta: "La licencia vence", tipo: "fecha" },
    { clave: "empresa_id", etiqueta: "Empresa con la que trabaja (opcional)", tipo: "empresa" },
  ],
}

const RUTA: Record<Clase, string> = {
  EMPRESA: "empresas",
  VEHICULO: "vehiculos",
  CHOFER: "choferes",
}
const NOMBRE: Record<Clase, string> = { EMPRESA: "empresa", VEHICULO: "vehículo", CHOFER: "chofer" }

type Fila = (Empresa | Vehiculo | Chofer) & Record<string, unknown>

export function useCatalogo<T>(clase: Clase) {
  return useQuery({
    queryKey: ["transporte", RUTA[clase]],
    queryFn: () => api.get<T[]>(`/api/v1/transporte/${RUTA[clase]}`),
  })
}

function titulo(clase: Clase, f: Fila, empresas: Empresa[]) {
  if (clase === "EMPRESA") {
    const e = f as Empresa
    return { arriba: e.razon_social, abajo: `RIF ${e.rif}` }
  }
  const empresa = empresas.find((e) => e.id === f.empresa_id)?.razon_social
  if (clase === "VEHICULO") {
    const v = f as Vehiculo
    const remolque = v.placa_remolque ? ` · remolque ${v.placa_remolque}` : ""
    return {
      arriba: `${v.placa}${remolque}`,
      abajo: [v.marca, v.modelo, empresa].filter(Boolean).join(" · "),
    }
  }
  const c = f as Chofer
  return {
    arriba: `${c.nombres} ${c.apellidos}`,
    abajo: [`C.I. ${c.cedula}`, empresa].filter(Boolean).join(" · "),
  }
}

/** Un catálogo del intermediario: sus empresas, sus vehículos o sus choferes, con sus documentos. */
export function Catalogo({ clase }: { clase: Clase }) {
  const filas = useCatalogo<Fila>(clase)
  const empresas = useCatalogo<Empresa>("EMPRESA")
  const [abierta, setAbierta] = useState<Fila | "nueva" | null>(null)

  if (filas.isPending) return <EstadoCarga etiqueta={`Cargando ${RUTA[clase]}`} />
  if (filas.isError)
    return (
      <EstadoError
        titulo="No se pudo cargar"
        error={filas.error}
        onReintentar={() => filas.refetch()}
      />
    )
  const lista = filas.data
  const sinEmpresas = clase !== "EMPRESA" && (empresas.data ?? []).length === 0

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Boton
          variante="primario"
          onClick={() => setAbierta("nueva")}
          disabled={clase === "VEHICULO" && sinEmpresas}
        >
          <Plus size={16} aria-hidden={true} /> Registrar {NOMBRE[clase]}
        </Boton>
      </div>
      {clase === "VEHICULO" && sinEmpresas && (
        <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
          Registre primero la empresa de transporte dueña del vehículo.
        </p>
      )}
      {lista.length === 0 ? (
        <EstadoVacio
          titulo={`Sin ${RUTA[clase]} registrados`}
          texto="Lo que registre aquí se elige al llenar un recibo."
        />
      ) : (
        <ul className="grid gap-2 sm:grid-cols-2">
          {lista.map((f) => {
            const t = titulo(clase, f, empresas.data ?? [])
            return (
              <li key={f.id} className="tarjeta flex items-start justify-between gap-2 p-3">
                <div className="min-w-0">
                  <p className="font-medium">{t.arriba}</p>
                  <p className="text-xs" style={{ color: "var(--fg-muted)" }}>
                    {t.abajo}
                  </p>
                  {!f.activo && <Insignia>Inactivo</Insignia>}
                </div>
                <Boton
                  variante="fantasma"
                  tamano="sm"
                  onClick={() => setAbierta(f)}
                  aria-label={`Editar ${t.arriba}`}
                >
                  <Pencil size={14} aria-hidden={true} /> Editar
                </Boton>
              </li>
            )
          })}
        </ul>
      )}
      {abierta && (
        <Ficha
          clase={clase}
          fila={abierta === "nueva" ? null : abierta}
          empresas={empresas.data ?? []}
          onCerrar={() => setAbierta(null)}
        />
      )}
    </div>
  )
}

function inicial(clase: Clase, fila: Fila | null): Record<string, string> {
  const datos: Record<string, string> = {}
  for (const c of CAMPOS[clase]) {
    const v = fila?.[c.clave]
    datos[c.clave] = v === null || v === undefined ? "" : String(v)
  }
  datos.activo = fila && !fila.activo ? "no" : "si"
  return datos
}

function Ficha({
  clase,
  fila,
  empresas,
  onCerrar,
}: {
  clase: Clase
  fila: Fila | null
  empresas: Empresa[]
  onCerrar: () => void
}) {
  const cliente = useQueryClient()
  const [id, setId] = useState<number | null>(fila?.id ?? null)
  const [datos, setDatos] = useState(() => inicial(clase, fila))
  const guardar = useMutation({
    mutationFn: () => {
      const cuerpo: Record<string, unknown> = { activo: datos.activo === "si" }
      for (const c of CAMPOS[clase]) {
        const v = (datos[c.clave] ?? "").trim()
        cuerpo[c.clave] =
          v === "" ? null : c.tipo === "numero" || c.tipo === "empresa" ? Number(v) : v
      }
      const ruta = `/api/v1/transporte/${RUTA[clase]}`
      return id ? api.put<Fila>(`${ruta}/${id}`, cuerpo) : api.post<Fila>(ruta, cuerpo)
    },
    onSuccess: (f) => {
      setId(f.id)
      cliente.invalidateQueries({ queryKey: ["transporte", RUTA[clase]] })
    },
  })
  const enviar = (e: FormEvent) => {
    e.preventDefault()
    guardar.mutate()
  }

  return (
    <Modal
      abierto={true}
      titulo={id ? `Ficha del ${NOMBRE[clase]}` : `Registrar ${NOMBRE[clase]}`}
      onCerrar={onCerrar}
      ancho="lg"
    >
      <form onSubmit={enviar} className="grid gap-3 sm:grid-cols-2">
        {CAMPOS[clase].map((c) => (
          <div key={c.clave} className="grid gap-1 text-sm">
            <label htmlFor={`campo-${clase}-${c.clave}`}>
              {c.etiqueta}
              {c.requerido && <span aria-hidden={true}> *</span>}
            </label>
            {c.tipo === "empresa" ? (
              <select
                id={`campo-${clase}-${c.clave}`}
                className="campo"
                required={c.requerido}
                value={datos[c.clave]}
                onChange={(e) => setDatos({ ...datos, [c.clave]: e.target.value })}
              >
                <option value="">{c.requerido ? "Elija…" : "Ninguna"}</option>
                {empresas.map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.razon_social}
                  </option>
                ))}
              </select>
            ) : (
              <input
                id={`campo-${clase}-${c.clave}`}
                className="campo"
                required={c.requerido}
                type={
                  c.tipo === "fecha"
                    ? "date"
                    : c.tipo === "numero"
                      ? "number"
                      : c.tipo === "correo"
                        ? "email"
                        : "text"
                }
                value={datos[c.clave]}
                onChange={(e) => setDatos({ ...datos, [c.clave]: e.target.value })}
              />
            )}
          </div>
        ))}
        {id && (
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={datos.activo === "si"}
              onChange={(e) => setDatos({ ...datos, activo: e.target.checked ? "si" : "no" })}
            />
            Activo (se ofrece al llenar recibos)
          </label>
        )}
        <div className="flex items-center justify-end gap-2 sm:col-span-2">
          {guardar.isError && (
            <p role="alert" className="mr-auto text-xs" style={{ color: "var(--destructive)" }}>
              {guardar.error instanceof ErrorApi ? guardar.error.message : "No se pudo guardar"}
            </p>
          )}
          {guardar.isSuccess && (
            <p role="status" className="mr-auto text-xs" style={{ color: "var(--success)" }}>
              Guardado.
            </p>
          )}
          <Boton type="submit" variante="primario" disabled={guardar.isPending}>
            {id ? "Guardar cambios" : "Registrar"}
          </Boton>
        </div>
      </form>
      <section className="mt-5">
        <h3 className="mb-2 text-sm font-semibold">Documentos</h3>
        {id ? (
          <Documentos dueno={clase} duenoId={id} />
        ) : (
          <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
            Registre la ficha y después suba sus documentos aquí mismo.
          </p>
        )}
      </section>
    </Modal>
  )
}
